<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { ActivityFeedService } from '@/core/activity-feed/activity-feed.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import type { ConversationIndex } from '@/core/conversations/conversation-index/conversation-index.type';
import MobileActivityRow from '@/components/Mobile/Activity/MobileActivityRow.vue';
import MobileActivityEmpty from '@/components/Mobile/Activity/MobileActivityEmpty.vue';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import CheckSquareBrokenSvg from '@/assets/svg/check-square-broken-16.svg?component';

const router = useRouter();
const activityFeedService = new ActivityFeedService();

const isLoading = ref(false);
const isLastPage = ref(false);
const isConfirmOpen = ref(false);
const isMarkingAllRead = ref(false);
const listElement = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const unreadCount = computed(() => activityFeedService.unreadMessageCount);
// The badge and mark-all-read action only appear when there is something unread,
// matching the empty-state design which shows neither.
const hasUnread = computed(() => unreadCount.value > 0);

const markAllReadSubtitle =
  'Are you sure? This will clear all unread messages from your activity feed. This action cannot be undone.';

// The shared service appends each fetched page to the store without de-duplicating,
// and a candidate can resurface on a later page as newer messages arrive between
// fetches. Collapse by conversationId so a candidate never renders twice.
const rows = computed(() => {
  const seen = new Set<number>();
  const deduped: ConversationIndex[] = [];
  for (const message of activityFeedService.unreadMessages) {
    if (seen.has(message.conversationId)) {
      continue;
    }
    seen.add(message.conversationId);
    deduped.push(message);
  }
  return deduped;
});

const showEmptyState = computed(() => !isLoading.value && rows.value.length === 0);
const isInitialLoading = computed(() => isLoading.value && rows.value.length === 0);

async function loadInitial(): Promise<void> {
  try {
    isLoading.value = true;
    isLastPage.value = false;
    activityFeedService.resetMessageList();
    await activityFeedService.fetchUnreadMessages();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not load your activity. Please refresh the page to try again.');
  } finally {
    isLoading.value = false;
  }
}

function openMarkAllReadConfirm(): void {
  isConfirmOpen.value = true;
}

async function confirmMarkAllRead(): Promise<void> {
  try {
    isMarkingAllRead.value = true;
    await activityFeedService.markAllAsRead();
    SnackbarService.success('All conversations marked as read');
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not mark all as read. Please try again.');
  } finally {
    // Always close: BaseModal is persistent, so leaving it open on error would
    // strand the user with no way to dismiss it.
    isMarkingAllRead.value = false;
    isConfirmOpen.value = false;
  }
}

async function loadMore(): Promise<void> {
  if (isLoading.value || isLastPage.value) {
    return;
  }
  const countBefore = activityFeedService.unreadMessages.length;
  try {
    isLoading.value = true;
    await activityFeedService.loadMoreMessages();
    // No new messages means we've reached the end; stop observing so the sentinel
    // doesn't keep re-triggering fetches of the (empty) next page.
    if (activityFeedService.unreadMessages.length === countBefore) {
      isLastPage.value = true;
      observer?.disconnect();
    }
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not load more activity. Please refresh the page to try again.');
  } finally {
    isLoading.value = false;
  }
}

// Reuses the desktop activity feed's deep-link contract: pushing `?conversationId=`
// lets the shared jobs router resolve the conversation's project and rewrite the URL
// to the canonical candidates path. The explicit `tab` is needed because the router's
// default tab is the resume tab; on mobile that segment maps to the Resume screen, so
// without it a tap would open the resume placeholder instead of the thread.
function openConversation(conversationId: number): void {
  router.push({
    path: '/jobs',
    query: { conversationId: String(conversationId), tab: 'conversation' },
  });
}

onMounted(async () => {
  // The badge reads the dedicated unread-count endpoint; it handles its own errors.
  void activityFeedService.fetchUnreadMessageCount();
  await loadInitial();
  // Wait for the rows (and their trailing sentinel) to render before observing.
  await nextTick();

  if (sentinel.value && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root: listElement.value, threshold: 0.1 },
    );
    observer.observe(sentinel.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section class="flex h-full flex-col bg-white" data-test="mobile-activity-screen">
    <header class="flex shrink-0 items-center justify-between bg-shade-860 px-4 pb-4 pt-5">
      <div class="flex items-center gap-2">
        <h1 class="font-serif text-lg font-bold leading-6 text-tint-0">Activity</h1>
        <span
          v-if="hasUnread"
          class="flex h-5 min-w-5 items-center justify-center rounded-full bg-highlight-50 px-1 text-sm font-black leading-[14px] text-highlight-500"
          data-test="mobile-activity-unread-badge"
        >
          {{ unreadCount }}
        </span>
      </div>

      <button
        v-if="hasUnread"
        type="button"
        class="flex items-center gap-1 text-xs font-bold leading-[18px] text-tint-0"
        data-test="mobile-activity-mark-all-read"
        @click="openMarkAllReadConfirm"
      >
        Mark all as read
        <CheckSquareBrokenSvg aria-hidden="true" class="h-4 w-4 text-tint-0" />
      </button>
    </header>

    <div ref="listElement" class="min-h-0 flex-1 overflow-y-auto">
      <MobileActivityEmpty v-if="showEmptyState" />

      <div v-else-if="isInitialLoading" class="flex h-full items-center justify-center">
        <SpinnerLoader />
      </div>

      <template v-else>
        <MobileActivityRow
          v-for="row in rows"
          :id="row.conversationId"
          :key="row.conversationId"
          :name="row.proName"
          :message-preview="row.lastMessageText"
          :timestamp="row.lastMessageSentTs"
          @clicked="openConversation"
        />
        <div ref="sentinel" class="flex h-8 items-center justify-center">
          <SpinnerLoader v-if="isLoading" />
        </div>
      </template>
    </div>

    <BaseModal
      v-model="isConfirmOpen"
      title="Mark all as read?"
      :subtitle="markAllReadSubtitle"
      :show-close-button="true"
      :show-cancel-button="true"
      :show-secondary-button="false"
      cancel-button-text="Cancel"
      submit-button-text="Mark all as read"
      :is-loading="isMarkingAllRead"
      @cancel="isConfirmOpen = false"
      @submit="confirmMarkAllRead"
    />
  </section>
</template>
